import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = window.location.origin;

function EmailVerificationPage({ onLogin }) {
  const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error', 'resend'
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  
  // Safe URL parameter extraction without useLocation to prevent crashes
  const getTokenFromURL = () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('token');
    } catch (error) {
      console.error('Error extracting token from URL:', error);
      return null;
    }
  };
  
  const navigateBack = () => {
    try {
      window.location.href = '/login';
    } catch (error) {
      console.error('Navigation error:', error);
      // Fallback: reload the page
      window.location.reload();
    }
  };

  useEffect(() => {
    const token = getTokenFromURL();
    if (token) {
      verifyEmail(token);
    } else {
      setStatus('error');
      setMessage('No verification token found in URL.');
    }
  }, []);

  const verifyEmail = async (verificationToken) => {
    try {
      setStatus('loading');
      
      const response = await axios.post(`${BACKEND_URL}/api/auth/verify-email`, {
        token: verificationToken
      });

      setStatus('success');
      setMessage(response.data.message);
      
      // Auto-login user after successful verification
      if (response.data.token && onLogin) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify({
          user_id: response.data.user_id,
          email: response.data.email,
          subscription_active: false,
          is_admin: false,
          admin_role: null
        }));

        // Redirect to homepage after showing success message (before any API failures)
        setTimeout(() => {
          console.log('✅ Email verification successful - redirecting to homepage');
          window.location.href = '/';
        }, 1500); // Reduced from 2000ms to 1500ms for faster redirect
      } else {
        // If no token, redirect to login page after showing message
        setTimeout(() => {
          console.log('📧 Email verification completed - redirecting to login');
          window.location.href = '/login';
        }, 2000);
      }

    } catch (err) {
      setStatus('error');
      setMessage(err.response?.data?.detail || 'Email verification failed');
    }
  };

  const handleResendVerification = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setMessage('Please enter your email address');
      return;
    }
    
    setResendLoading(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/auth/resend-verification`, {
        email: email
      });
      
      setMessage(`Verification email sent to ${email}. Please check your inbox.`);
      setStatus('resend');
      
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Failed to resend verification email');
    } finally {
      setResendLoading(false);
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
            <h2 className="text-2xl font-bold text-white mb-4">Verifying Your Email...</h2>
            <p className="text-gray-300">Please wait while we verify your email address.</p>
          </div>
        );
        
      case 'success':
        return (
          <div className="text-center">
            <div className="text-6xl mb-6">✅</div>
            <h2 className="text-2xl font-bold text-green-400 mb-4">Email Verified Successfully!</h2>
            <p className="text-gray-300 mb-6">{message}</p>
            <p className="text-sm text-gray-400">You will be redirected to your dashboard in a moment...</p>
          </div>
        );
        
      case 'error':
        return (
          <div className="text-center">
            <div className="text-6xl mb-6">❌</div>
            <h2 className="text-2xl font-bold text-red-400 mb-4">Verification Failed</h2>
            <p className="text-gray-300 mb-8">{message}</p>
            
            <div className="bg-yellow-900 border border-yellow-700 p-4 rounded-lg mb-6">
              <p className="text-yellow-100 text-sm mb-4">
                Need a new verification link? Enter your email below:
              </p>
              
              <form onSubmit={handleResendVerification} className="space-y-4">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="w-full px-3 py-2 border border-gray-600 placeholder-gray-400 text-white bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
                <button
                  type="submit"
                  disabled={resendLoading}
                  className="w-full py-2 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resendLoading ? 'Sending...' : 'Resend Verification Email'}
                </button>
              </form>
            </div>
            
            <button
              onClick={navigateBack}
              className="text-blue-400 hover:text-blue-300 text-sm"
            >
              ← Back to Login
            </button>
          </div>
        );
        
      case 'resend':
        return (
          <div className="text-center">
            <div className="text-6xl mb-6">📧</div>
            <h2 className="text-2xl font-bold text-blue-400 mb-4">Verification Email Sent!</h2>
            <p className="text-gray-300 mb-8">{message}</p>
            
            <button
              onClick={navigateBack}
              className="text-blue-400 hover:text-blue-300 text-sm"
            >
              ← Back to Login
            </button>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-2">
            🤖 Grid Trading Bot
          </h1>
          <p className="text-gray-400">Email Verification</p>
        </div>
        
        <div className="bg-gray-800 rounded-lg p-8 shadow-lg">
          {renderContent()}
        </div>
        
        <div className="text-center text-xs text-gray-500">
          <p>Version 2.9.0 • Secure email verification</p>
        </div>
      </div>
    </div>
  );
}

export default EmailVerificationPage;